/**
 * TooltipAnchorContext — Registration-based lifecycle for tooltip anchors
 * 
 * See: docs/invariants/LIFECYCLE_OWNERSHIP.md
 * 
 * This context provides a centralized registry for tooltip anchor elements.
 * Anchors register on mount and unregister on unmount, ensuring the tooltip
 * system never holds stale DOM references.
 * 
 * IMPORTANT: The registration API is internal. Only `useTooltipAnchor` hook
 * should be used by anchor components.
 */

import { createContext, useContext, useCallback, useRef, useState, ReactNode } from 'react';

type AnchorRegistry = Map<string, HTMLElement>;

interface TooltipAnchorContextValue {
  anchors: AnchorRegistry;
  register: (id: string, el: HTMLElement) => void;
  unregister: (id: string) => void;
}

const TooltipAnchorContext = createContext<TooltipAnchorContextValue | null>(null);

export function TooltipAnchorProvider({ children }: { children: ReactNode }) {
  // Use ref to avoid recreating Map on every render
  const anchorsRef = useRef<AnchorRegistry>(new Map());
  
  // Force re-render when registry changes (for consumers to get updated anchors)
  const [, setVersion] = useState(0);

  const register = useCallback((id: string, el: HTMLElement) => {
    anchorsRef.current.set(id, el);
    setVersion(v => v + 1);
  }, []);

  const unregister = useCallback((id: string) => {
    anchorsRef.current.delete(id);
    setVersion(v => v + 1);
  }, []);

  return (
    <TooltipAnchorContext.Provider value={{ 
      anchors: anchorsRef.current, 
      register, 
      unregister 
    }}>
      {children}
    </TooltipAnchorContext.Provider>
  );
}

/**
 * Internal hook — used only by useTooltipAnchor and useTooltipGuide
 * Do not export or use directly in components
 */
export function useTooltipAnchorRegistry() {
  const ctx = useContext(TooltipAnchorContext);
  if (!ctx) {
    throw new Error('useTooltipAnchorRegistry must be used within TooltipAnchorProvider');
  }
  return ctx;
}
